import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  model,
  viewChildren,
} from '@angular/core';
import { FormValueControl } from '@angular/forms/signals';

@Component({
  selector: 'shared-ui-code-input',
  templateUrl: './code-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CodeInputComponent implements FormValueControl<string> {
  /** Positional: an empty field is a space, so `'12 4'` keeps its gap. Complete means only digits. */
  readonly value = model('');
  readonly touched = model(false);
  readonly length = input(6);
  readonly invalid = input(false);
  readonly disabled = input(false);
  readonly ariaLabel = input('Bestätigungscode');

  private readonly inputs = viewChildren<ElementRef<HTMLInputElement>>('field');

  protected readonly fields = computed(() => {
    const value = this.value();
    return Array.from({ length: this.length() }, (_, index) => ({
      index,
      digit: /\d/.test(value.charAt(index)) ? value.charAt(index) : '',
      label: `Ziffer ${index + 1}`,
    }));
  });

  protected readonly fieldClass = computed(() =>
    this.invalid()
      ? 'border-red-400 focus:bg-red-100 focus:ring-red-400'
      : 'border-gray-300 focus:ring-theme-secondary-gray',
  );

  protected onInput(event: Event, index: number): void {
    const field = event.target as HTMLInputElement;
    const typed = field.value.replace(/\D/g, '');
    const previous = this.fields()[index].digit;
    // typing into a filled field without a selection adds the new digit before or after the old one
    const incoming =
      previous && typed.length === 2 ? typed.replace(previous, '') || previous : typed;

    field.value = incoming.charAt(0);
    this.fill(index, incoming);
  }

  protected onPaste(event: ClipboardEvent, index: number): void {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '');
    if (pasted) this.fill(index, pasted);
  }

  protected onBackspace(event: Event, index: number): void {
    if (index === 0 || this.fields()[index].digit) return;

    event.preventDefault();
    this.fill(index - 1, '');
    this.inputs()[index - 1].nativeElement.focus();
  }

  protected selectContent(event: FocusEvent): void {
    (event.target as HTMLInputElement).select();
  }

  protected onFocusOut(event: FocusEvent): void {
    // the auto-advance to the next field must not count as leaving the control
    const group = event.currentTarget as HTMLElement;
    if (!group.contains(event.relatedTarget as Node | null)) this.touched.set(true);
  }

  private fill(start: number, incoming: string): void {
    const chars = incoming ? incoming.slice(0, this.length() - start).split('') : [''];
    const digits = this.fields().map(({ index, digit }) =>
      index >= start && index < start + chars.length ? chars[index - start] : digit,
    );
    this.value.set(
      digits
        .map((digit) => digit || ' ')
        .join('')
        .trimEnd(),
    );

    if (incoming) this.moveFocus(start, start + chars.length);
  }

  private moveFocus(from: number, to: number): void {
    const next = this.inputs()[to];
    if (next) {
      next.nativeElement.focus();
    } else {
      this.inputs()[from].nativeElement.blur();
    }
  }
}
